<script setup lang="ts">
/**
 * Prose, read or written in place.
 *
 * The one place in the application that writes authored markup into the page,
 * which makes it the one place where sanitising is not optional. The HTML is
 * cleaned on the way out every time rather than trusted because it was cleaned
 * once on import: a report can be edited by anyone it has been shared with,
 * and an import is not the only way markup arrives.
 *
 * The editor is mounted only for the selected section. Mounting one per text
 * block would put a live editor behind every paragraph of a long report, each
 * with its own document model and listeners.
 *
 * How the block is set (columns, gap and rule, alignment, hyphenation,
 * leading, a frame, a drop cap; see `domain/prose.ts`) is given to one element
 * around both, as variables and classes, so it reads and writes alike.
 */
import { useI18n } from 'vue-i18n'

import { computed } from 'vue'

import RichTextEditor from '@/components/builder/RichTextEditor.vue'
import { textStyleClassName } from '@/domain/layout/textStyles'
import { textSettingClasses, textSettingStyle } from '@/domain/prose'
import { isEmptyHtml, sanitiseHtml } from '@/domain/sanitise'
import type { TextSection } from '@/domain/section'

const { t } = useI18n()

const props = withDefaults(
  defineProps<{
    section: TextSection
    /** Selected, and the report is open for changes. */
    editing?: boolean
  }>(),
  { editing: false },
)

const emit = defineEmits<{
  change: [html: string]
}>()

const safe = computed(() => sanitiseHtml(props.section.html))
const empty = computed(() => isEmptyHtml(props.section.html))

/**
 * The class the block's own named style is given, or nothing.
 *
 * Applied to the block as a whole — reading or editing alike — rather than
 * only wherever the toolbar's per-selection mark reaches: this is the
 * block's default, and a mark nested inside it still overrides it exactly as
 * CSS colour and font-family always cascade.
 */
const blockStyleClass = computed(() =>
  props.section.textStyleId ? textStyleClassName(props.section.textStyleId) : '',
)

const settingStyle = computed(() => textSettingStyle(props.section))
const settingClasses = computed(() => textSettingClasses(props.section))
</script>

<template>
  <div class="text" :class="settingClasses" :style="settingStyle">
    <RichTextEditor
      v-if="props.editing"
      :html="section.html"
      :class="blockStyleClass"
      autofocus
      @change="emit('change', $event)"
    />

    <p v-else-if="empty" class="text__empty">{{ t('canvas.emptyText') }}</p>

    <!--
      Sanitised against an allowlist immediately above; see domain/sanitise.ts
      for exactly what survives. This is the only v-html in the application,
      and it is why that module has tests of its own.
    -->
    <!-- eslint-disable vue/no-v-html -->
    <div v-else v-html="safe" class="text__prose" :class="blockStyleClass" />
    <!-- eslint-enable vue/no-v-html -->
  </div>
</template>

<style scoped>
.text__prose {
  column-count: var(--text-columns, 1);
  column-gap: var(--text-gap, 10mm);
  column-rule: var(--text-rule, none);
  line-height: var(--text-leading, inherit);
}

.text__prose :deep(p) {
  break-inside: avoid-column;
  hyphens: var(--text-hyphens, auto);
}

.text__empty {
  color: var(--paper-ink-soft);
  font-style: italic;
}

@media print {
  .text__empty {
    display: none;
  }
}
</style>
